import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonLoading,
    IonModal,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonTextarea,
    IonTitle,
    IonToast,
    IonToolbar,
    useIonViewDidEnter
} from '@ionic/react';
import {
    addOutline,
    arrowBack,
    checkmarkCircleOutline,
    closeOutline,
    receiptOutline,
    trashOutline
} from 'ionicons/icons';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MessageContext } from '../../../../../contexts/MessageContext';
import {
    fiscalBuscarConfiguracao,
    fiscalEmitirNFCe,
    fiscalListarCategorias,
    requestService
} from '../../../../../Utils/Services';
import './FiscalEmissao.css';

const TIPOS_PAGAMENTO = [
    { valor: "Dinheiro", label: "Dinheiro" },
    { valor: "Cheque", label: "Cheque" },
    { valor: "Cartao_Credito", label: "Cartão de Crédito" },
    { valor: "Cartao_Debito", label: "Cartão de Débito" },
    { valor: "Credito_Loja", label: "Crédito Loja" },
    { valor: "Vale_Alimentacao", label: "Vale Alimentação" },
    { valor: "Vale_Refeicao", label: "Vale Refeição" },
    { valor: "Pix", label: "PIX" },
    { valor: "Outros", label: "Outros" },
];

type Props = {
    abrirMenu: boolean;
};

interface ItemNFCe {
    categoriaId?: string;
    codigo: string;
    descricao: string;
    ncm: string;
    cfop: string;
    unidade: string;
    quantidade: number;
    valorUnitario: number;
    valorTotal: number;
}

interface PagamentoNFCe {
    tipo: string;
    valor: number;
}

const FiscalEmissao: React.FC<Props> = (props: Props) => {
    const history = useHistory();
    const { loja } = useContext(MessageContext);
    const { dadosEmpresa } = useContext(MessageContext);
    const { esconderMenu, setEsconderMenu } = useContext(MessageContext);
    const { nomeMenu, setNomeMenu } = useContext(MessageContext);

    // Loading & Toasts
    const [showLoading, setShowLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Carregando...");
    const [showToast, setShowToast] = useState(false);
    const [textToast, setTextToast] = useState("");
    const [toastColor, setToastColor] = useState("success");

    // Config fiscal
    const [configFiscal, setConfigFiscal] = useState<any>(null);

    // Categorias fiscais
    const [categorias, setCategorias] = useState<any[]>([]);

    // Destinatário (opcional)
    const [cpfDestinatario, setCpfDestinatario] = useState("");

    // Itens
    const [itens, setItens] = useState<ItemNFCe[]>([]);
    const [showAddItem, setShowAddItem] = useState(false);
    const [novoItem, setNovoItem] = useState<ItemNFCe>({
        categoriaId: "", codigo: "", descricao: "", ncm: "", cfop: "5102", unidade: "UN", quantidade: 1, valorUnitario: 0, valorTotal: 0
    });

    // Pagamentos
    const [pagamentos, setPagamentos] = useState<PagamentoNFCe[]>([]);
    const [showAddPagamento, setShowAddPagamento] = useState(false);
    const [novoPagamento, setNovoPagamento] = useState<PagamentoNFCe>({ tipo: "Dinheiro", valor: 0 });

    // Info complementar
    const [infoComplementar, setInfoComplementar] = useState("");

    // Resultado
    const [emissaoSucesso, setEmissaoSucesso] = useState(false);
    const [resultadoNota, setResultadoNota] = useState<any>(null);

    // Trava duplo clique
    const [emitindo, setEmitindo] = useState(false);

    const [acessos] = useState<any>(JSON.parse(window.localStorage.getItem("acessos") || '{}'));

    useIonViewDidEnter(() => {
        if (acessos?.fiscal != 'leitura' && acessos?.fiscal != 'edicao') {
            history.replace('/empresa/home');
            return;
        }
        setEsconderMenu(true);
        setNomeMenu("");
        carregarConfig();
        carregarCategorias();
        resetForm();
    });

    function resetForm() {
        setItens([]);
        setPagamentos([]);
        setCpfDestinatario("");
        setInfoComplementar("");
        setEmissaoSucesso(false);
        setResultadoNota(null);
        setEmitindo(false);
    }

    function carregarConfig() {
        if (!loja) return;
        if (!dadosEmpresa?.modulos?.modulo_fiscal) return;
        requestService(
            fiscalBuscarConfiguracao.url.replace("{empresaId}", loja.replaceAll(/#/g, '%23')),
            { method: fiscalBuscarConfiguracao.method, credentials: 'include' },
            history,
            (response: any) => {
                setConfigFiscal(response.dados || response);
            },
            (error: any) => {
                mostrarToast("Configure os dados fiscais antes de emitir.", "warning");
            }
        );
    }

    function carregarCategorias() {
        if (!loja) return;
        requestService(
            fiscalListarCategorias.url.replace("{empresaId}", loja.replaceAll(/#/g, '%23')),
            { method: fiscalListarCategorias.method, credentials: 'include' },
            history,
            (response: any) => {
                setCategorias(response.dados || []);
            },
            () => { /* sem categorias — campos manuais ainda funcionam */ }
        );
    }

    // =============================
    // ITENS
    // =============================
    function adicionarItem() {
        if (!novoItem.descricao) { mostrarToast("Informe a descrição do item.", "warning"); return; }
        if (!novoItem.ncm || novoItem.ncm.length !== 8) { mostrarToast("NCM deve ter 8 dígitos.", "warning"); return; }
        if (novoItem.quantidade <= 0) { mostrarToast("Quantidade deve ser maior que zero.", "warning"); return; }
        if (novoItem.valorUnitario <= 0) { mostrarToast("Valor unitário deve ser maior que zero.", "warning"); return; }

        const itemComTotal = {
            ...novoItem,
            valorTotal: novoItem.quantidade * novoItem.valorUnitario
        };
        setItens([...itens, itemComTotal]);
        setNovoItem({ categoriaId: "", codigo: "", descricao: "", ncm: "", cfop: "5102", unidade: "UN", quantidade: 1, valorUnitario: 0, valorTotal: 0 });
        setShowAddItem(false);
    }

    function removerItem(index: number) {
        setItens(itens.filter((_, i) => i !== index));
    }

    // =============================
    // PAGAMENTOS
    // =============================
    function adicionarPagamento() {
        if (novoPagamento.valor <= 0) { mostrarToast("Valor do pagamento deve ser maior que zero.", "warning"); return; }

        setPagamentos([...pagamentos, { ...novoPagamento }]);
        setNovoPagamento({ tipo: "Dinheiro", valor: 0 });
        setShowAddPagamento(false);
    }

    function removerPagamento(index: number) {
        setPagamentos(pagamentos.filter((_, i) => i !== index));
    }

    // =============================
    // CÁLCULOS
    // =============================
    function calcularTotalItens() {
        return itens.reduce((total, item) => total + (item.quantidade * item.valorUnitario), 0);
    }

    function calcularTotalPagamentos() {
        return pagamentos.reduce((total, pag) => total + pag.valor, 0);
    }

    // =============================
    // EMISSÃO
    // =============================
    function emitirNFCe() {
        if (emitindo) return;

        if (!configFiscal) {
            mostrarToast("Configure os dados fiscais primeiro.", "warning");
            return;
        }

        if (itens.length === 0) {
            mostrarToast("Adicione pelo menos um item.", "warning");
            return;
        }
        if (pagamentos.length === 0) {
            mostrarToast("Adicione pelo menos um pagamento.", "warning");
            return;
        }

        const totalItens = calcularTotalItens();
        const totalPagamentos = calcularTotalPagamentos();

        if (Math.abs(totalItens - totalPagamentos) > 0.01) {
            mostrarToast(`Total dos itens (${formatarValor(totalItens)}) difere do total dos pagamentos (${formatarValor(totalPagamentos)}).`, "warning");
            return;
        }

        setEmitindo(true);
        setShowLoading(true);
        setLoadingMessage("Emitindo nota fiscal...");

        const body: any = {
            empresaId: loja,
            itens: itens.map(item => ({
                codigo: item.codigo || "",
                descricao: item.descricao,
                ncm: item.ncm,
                cfop: item.cfop,
                unidade: item.unidade,
                quantidade: item.quantidade,
                valorUnitario: item.valorUnitario,
                valorTotal: item.valorTotal || (item.quantidade * item.valorUnitario)
            })),
            pagamentos: pagamentos.map(pag => ({
                tipo: pag.tipo,
                valor: pag.valor
            })),
            serie: configFiscal.serie || "1"
        };

        if (cpfDestinatario) {
            body.destinatario = { cpf: cpfDestinatario.replace(/\D/g, '') };
        }
        if (infoComplementar) {
            body.infoComplementar = infoComplementar;
        }

        requestService(
            fiscalEmitirNFCe.url,
            {
                method: fiscalEmitirNFCe.method,
                body: JSON.stringify(body),
                credentials: 'include',
                timeout: 60000
            },
            history,
            (response: any) => {
                setShowLoading(false);
                setEmitindo(false);

                const dados = response.dados || response;
                if (response.sucesso || dados.status === 100) {
                    setResultadoNota(dados);
                    setEmissaoSucesso(true);

                    // Guardar localmente
                    // try {
                    //     const notasSalvas = JSON.parse(localStorage.getItem("ultimasNFCe") || "[]");
                    //     notasSalvas.unshift({
                    //         nfceId: dados.nfceId,
                    //         chave: dados.chave,
                    //         numero: dados.numero,
                    //         data: new Date().toISOString()
                    //     });
                    //     localStorage.setItem("ultimasNFCe", JSON.stringify(notasSalvas.slice(0, 50)));
                    // } catch (e) { }

                    mostrarToast("NFC-e emitida com sucesso!", "success");
                } else {
                    // resposta 2xx mas com sucesso:false (raro na API fiscal, cobre casos futuros)
                    const msgFallback = dados.mensagem || dados.motivo || dados.message || response.message || "Erro ao emitir NFC-e.";
                    mostrarToast(msgFallback, "danger");
                }
            },
            (error: any) => {
                setShowLoading(false);
                setEmitindo(false);
                // error.mensagem: campo real da SEFAZ (ex: "Rejeição: CPF do destinatário inválido")
                // error.message: mensagem genérica gerada pelo requestService
                // error.status: pode ser string (ex: "237") por causa do spread do body da fiscal API
                const msgSefaz = error.mensagem || error.message || "Erro ao emitir NFC-e.";
                mostrarToast(msgSefaz, "danger");
            }
        );
    }

    function formatarValor(valor: number) {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
    }

    function novaEmissao() {
        resetForm();
    }

    function copiarChave() {
        if (resultadoNota?.chave) {
            navigator.clipboard.writeText(resultadoNota.chave).then(() => {
                mostrarToast("Chave copiada!", "success");
            });
        }
    }

    function mostrarToast(msg: string, color: string) {
        setTextToast(msg);
        setToastColor(color);
        setShowToast(true);
    }

    function getLabelTipoPagamento(tipo: string) {
        return TIPOS_PAGAMENTO.find(t => t.valor === tipo)?.label || tipo;
    }

    // ============= TELA DE SUCESSO =============
    if (emissaoSucesso && resultadoNota) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar color="primary">
                        <IonButtons slot="start">
                            <IonBackButton icon={arrowBack} text='' />
                        </IonButtons>
                        <IonTitle>NFC-e Emitida</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <IonContent color="light">
                    <div className="fiscal-sucesso-container">
                        <IonIcon icon={checkmarkCircleOutline} />
                        <h2>NFC-e emitida com sucesso!</h2>
                        <div className="nota-info">
                            <p><strong>Nº:</strong> {resultadoNota.numero}</p>
                            <p><strong>Protocolo:</strong> {resultadoNota.protocolo}</p>
                            <p><strong>Valor:</strong> {formatarValor(calcularTotalItens())}</p>
                            {resultadoNota.chave && (
                                <p className="chave">Chave: {resultadoNota.chave}</p>
                            )}
                        </div>
                        <div style={{ display: "flex", gap: "8px", margin: "8px 0" }}>
                            <IonButton expand="block" fill="outline" color="primary" onClick={copiarChave} style={{ flex: 1 }}>

                                Copiar chave
                            </IonButton>
                            {navigator.share && (
                                <IonButton expand="block" fill="outline" color="primary" onClick={() => {
                                    navigator.share({
                                        title: `NFC-e nº ${resultadoNota.numero}`,
                                        text: `Chave: ${resultadoNota.chave}`,
                                    }).catch(() => { });
                                }} style={{ flex: 1 }}>
                                    Compartilhar
                                </IonButton>
                            )}
                        </div>
                        <IonButton expand="block" onClick={novaEmissao}>
                            Nova emissão
                        </IonButton>
                        <IonButton expand="block" fill="outline" onClick={() => history.push('/empresa/fiscalhistorico')}>
                            Ver histórico
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    // ============= FORMULÁRIO DE EMISSÃO =============
    return (
        <IonPage>
            <IonHeader>
                <IonToolbar color="primary">
                    <IonButtons slot="start">
                        <IonBackButton icon={arrowBack} text='' />
                    </IonButtons>
                    <IonTitle>Emitir NFC-e</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="fiscal-emissao-container" color="light">

                {/* Destinatário (opcional) */}
                <div className="fiscal-emissao-section">
                    <div className="section-header">Destinatário (opcional)</div>
                    <IonCard style={{ margin: "8px 16px", borderRadius: "8px" }}>
                        <IonCardContent>
                            <IonItem>
                                <IonLabel position="stacked">CPF do cliente</IonLabel>
                                <IonInput
                                    value={cpfDestinatario}
                                    placeholder="Opcional em NFC-e"
                                    onIonChange={(e) => setCpfDestinatario(e.detail.value || "")}
                                    maxlength={14}
                                />
                            </IonItem>
                        </IonCardContent>
                    </IonCard>
                </div>

                {/* Itens */}
                <div className="fiscal-emissao-section">
                    <div className="section-header">
                        <IonIcon icon={receiptOutline} />
                        Itens ({itens.length})
                    </div>

                    {itens.map((item, index) => (
                        <div key={index} className="fiscal-item-card">
                            <div className="fiscal-item-card-header">
                                <span className="fiscal-item-card-nome">{item.descricao}</span>
                                <IonIcon icon={trashOutline} className="fiscal-item-card-remove" onClick={() => removerItem(index)} />
                            </div>
                            <div className="fiscal-item-card-info">
                                <span>NCM: {item.ncm}</span>
                                <span>CFOP: {item.cfop}</span>
                                <span>{item.quantidade} x {formatarValor(item.valorUnitario)}</span>
                                <span><strong>{formatarValor(item.quantidade * item.valorUnitario)}</strong></span>
                            </div>
                        </div>
                    ))}

                    <IonButton disabled={acessos?.fiscal != 'edicao'} expand="block" fill="outline" className="fiscal-add-item-btn" onClick={() => setShowAddItem(true)}>
                        <IonIcon slot="start" icon={addOutline} />
                        Adicionar item
                    </IonButton>
                </div>

                {/* Pagamentos */}
                <div className="fiscal-emissao-section">
                    <div className="section-header">Pagamentos ({pagamentos.length})</div>

                    {pagamentos.map((pag, index) => (
                        <div key={index} className="fiscal-item-card">
                            <div className="fiscal-item-card-header">
                                <span className="fiscal-pagamento-chip">{getLabelTipoPagamento(pag.tipo)}</span>
                                <IonIcon icon={trashOutline} className="fiscal-item-card-remove" onClick={() => removerPagamento(index)} />
                            </div>
                            <div className="fiscal-item-card-info">
                                <span><strong>{formatarValor(pag.valor)}</strong></span>
                            </div>
                        </div>
                    ))}

                    <IonButton disabled={acessos?.fiscal != 'edicao'} expand="block" fill="outline" className="fiscal-add-item-btn" onClick={() => setShowAddPagamento(true)}>
                        <IonIcon slot="start" icon={addOutline} />
                        Adicionar pagamento
                    </IonButton>
                </div>

                {/* Info complementar */}
                <div className="fiscal-emissao-section">
                    <div className="section-header">Informações complementares</div>
                    <IonCard style={{ margin: "8px 16px", borderRadius: "8px" }}>
                        <IonCardContent>
                            <IonTextarea
                                value={infoComplementar}
                                placeholder="Texto livre (opcional)"
                                onIonChange={(e) => setInfoComplementar(e.detail.value || "")}
                                rows={3}
                            />
                        </IonCardContent>
                    </IonCard>
                </div>

                {/* Botão emitir */}
                <div className="fiscal-emitir-btn">
                    <div
                        title={!dadosEmpresa?.modulos?.modulo_fiscal ? "Módulo fiscal não foi contratado." : undefined}
                        style={{ height: '100%' }}
                    >
                        <IonButton disabled={acessos?.fiscal != 'edicao' || !dadosEmpresa?.modulos?.modulo_fiscal || emitindo} expand="block" size="large" onClick={emitirNFCe}>
                            {emitindo ? "Emitindo..." : "Emitir NFC-e"}
                        </IonButton>
                    </div>
                </div>

                {/* Modal Adicionar Item */}
                <IonModal isOpen={showAddItem} onDidDismiss={() => setShowAddItem(false)}>
                    <IonHeader>
                        <IonToolbar color="primary">
                            <IonButtons slot="start">
                                <IonButton onClick={() => setShowAddItem(false)}>
                                    <IonIcon icon={closeOutline} />
                                </IonButton>
                            </IonButtons>
                            <IonTitle>Adicionar Item</IonTitle>
                            <IonButtons slot="end">
                                <IonButton onClick={adicionarItem}>Salvar</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent>
                        <IonList>
                            {/* Seletor de categoria fiscal — preenche NCM e CFOP automaticamente */}
                            {categorias.length > 0 && (
                                <IonItem>
                                    <IonLabel position="stacked">Categoria fiscal *</IonLabel>
                                    <IonSelect
                                        value={novoItem.categoriaId || ""}
                                        placeholder="Selecione a categoria"
                                        interface="action-sheet"
                                        onIonChange={e => {
                                            const cat = categorias.find((c: any) => c.id === e.detail.value);
                                            if (cat) {
                                                setNovoItem({ ...novoItem, categoriaId: cat.id, ncm: cat.ncm, cfop: cat.cfop });
                                            }
                                        }}
                                    >
                                        {categorias.map((cat: any) => (
                                            <IonSelectOption key={cat.id} value={cat.id}>
                                                {cat.nome} — NCM {cat.ncm}
                                            </IonSelectOption>
                                        ))}
                                    </IonSelect>
                                </IonItem>
                            )}
                            <IonItem>
                                <IonLabel position="stacked">Código do produto</IonLabel>
                                <IonInput value={novoItem.codigo} onIonChange={(e) => setNovoItem({ ...novoItem, codigo: e.detail.value || "" })} placeholder="Código interno (opcional)" />
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">Descrição *</IonLabel>
                                <IonInput value={novoItem.descricao} onIonChange={(e) => setNovoItem({ ...novoItem, descricao: e.detail.value || "" })} placeholder="Nome do produto" />
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">
                                    NCM{novoItem.categoriaId ? " (da categoria)" : " (8 dígitos) *"}
                                </IonLabel>
                                <IonInput
                                    value={novoItem.ncm}
                                    readonly={!!novoItem.categoriaId}
                                    onIonChange={e => { if (!novoItem.categoriaId) setNovoItem({ ...novoItem, ncm: e.detail.value || "" }); }}
                                    placeholder="8 dígitos"
                                    maxlength={8}
                                />
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">
                                    CFOP{novoItem.categoriaId ? " (da categoria)" : " *"}
                                </IonLabel>
                                <IonInput
                                    value={novoItem.cfop}
                                    readonly={!!novoItem.categoriaId}
                                    onIonChange={e => { if (!novoItem.categoriaId) setNovoItem({ ...novoItem, cfop: e.detail.value || "" }); }}
                                    placeholder="Ex: 5102"
                                />
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">Unidade</IonLabel>
                                <IonSelect value={novoItem.unidade} onIonChange={(e) => setNovoItem({ ...novoItem, unidade: e.detail.value })}>
                                    <IonSelectOption value="UN">UN - Unidade</IonSelectOption>
                                    <IonSelectOption value="KG">KG - Quilograma</IonSelectOption>
                                    <IonSelectOption value="LT">LT - Litro</IonSelectOption>
                                    <IonSelectOption value="MT">MT - Metro</IonSelectOption>
                                    <IonSelectOption value="PC">PC - Peça</IonSelectOption>
                                    <IonSelectOption value="CX">CX - Caixa</IonSelectOption>
                                </IonSelect>
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">Quantidade *</IonLabel>
                                <IonInput type="number" value={novoItem.quantidade} onIonChange={(e) => setNovoItem({ ...novoItem, quantidade: parseFloat(e.detail.value || "0") })} min="0.01" step="0.01" />
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">Valor Unitário (R$) *</IonLabel>
                                <IonInput type="number" value={novoItem.valorUnitario} onIonChange={(e) => setNovoItem({ ...novoItem, valorUnitario: parseFloat(e.detail.value || "0") })} min="0.01" step="0.01" />
                            </IonItem>
                        </IonList>
                    </IonContent>
                </IonModal>

                {/* Modal Adicionar Pagamento */}
                <IonModal isOpen={showAddPagamento} onDidDismiss={() => setShowAddPagamento(false)}>
                    <IonHeader>
                        <IonToolbar color="primary">
                            <IonButtons slot="start">
                                <IonButton onClick={() => setShowAddPagamento(false)}>
                                    <IonIcon icon={closeOutline} />
                                </IonButton>
                            </IonButtons>
                            <IonTitle>Adicionar Pagamento</IonTitle>
                            <IonButtons slot="end">
                                <IonButton onClick={adicionarPagamento}>Salvar</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent>
                        <IonList>
                            <IonItem>
                                <IonLabel position="stacked">Tipo de pagamento *</IonLabel>
                                <IonSelect value={novoPagamento.tipo} onIonChange={(e) => setNovoPagamento({ ...novoPagamento, tipo: e.detail.value })}>
                                    {TIPOS_PAGAMENTO.map(tp => (
                                        <IonSelectOption key={tp.valor} value={tp.valor}>{tp.label}</IonSelectOption>
                                    ))}
                                </IonSelect>
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">Valor (R$) *</IonLabel>
                                <IonInput type="number" value={novoPagamento.valor} onIonChange={(e) => setNovoPagamento({ ...novoPagamento, valor: parseFloat(e.detail.value || "0") })} min="0.01" step="0.01" />
                            </IonItem>
                        </IonList>
                    </IonContent>
                </IonModal>

                <IonLoading isOpen={showLoading} message={loadingMessage} />

                <IonToast
                    isOpen={showToast}
                    position="top"
                    onDidDismiss={() => setShowToast(false)}
                    message={textToast}
                    duration={3000}
                    color={toastColor}
                />
            </IonContent>

            {/* Footer com total */}
            {itens.length > 0 && (
                <IonFooter className="ion-no-border">
                    <div className="fiscal-total-bar">
                        <span className="fiscal-total-label">Total dos itens</span>
                        <span className="fiscal-total-valor">{formatarValor(calcularTotalItens())}</span>
                    </div>
                </IonFooter>
            )}
        </IonPage>
    );
};

export default FiscalEmissao;
